import moment from 'moment';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';

export const formatStr = (str) => {
  return str === 'NULL' || str === 'UNKNOWN' ? '' : str;
};

export const formatDate = (value) => {
  const date = moment(value || null);
  return date.isValid() ? date.format('MM/DD/YYYY') : '';
};


export const formatDateLocale = (value) => {
  if (!value) return '';

  try {
    let date;

    // Handle different date formats properly
    if (typeof value === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        // For date-only strings, parse as local date to avoid timezone issues
        const [year, month, day] = value.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        // For datetime strings, parse normally
        date = new Date(value);
      }
    } else {
      // For Date objects or other formats
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  } catch (error) {
    // Fallback to moment formatting if there's any error
    const momentDate = moment(value);
    return momentDate.isValid() ? momentDate.format('MM/DD/YYYY') : '';
  }
};

export const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const getFile = (base64 = '', type = 'excel', title = '', name = '') => {
  if (!base64) {
    return;
  }

  const str = title?.length >= 20 ? title.split(' ')[0] : title;
  let filename = name || `${str} ${moment().format('MM.DD.YYYY')}`;

  const a = document.createElement('a');
  let headerFile;
  if (type === 'csv') {
    headerFile = 'data:attachment/csv';
    filename = filename.concat('.csv');
  } else if (type === 'excel' || type === 'xlsx') {
    headerFile = 'data:application/vnd.ms-excel';
    filename = filename.concat('.xlsx');
  }
  a.href = `${headerFile};base64, ${base64}`;
  a.download = filename;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const getFileDeprecated = (
  items = [],
  cols = [],
  type = 'excel',
  title = '',
  name = ''
) => {
  if (!items.length || !cols.length) {
    return;
  }

  const str = title?.length >= 20 ? title.split(' ')[0] : title;
  const filename = name || `${str} ${moment().format('MM.DD.YYYY')}`;

  const workSheet = XLSX.utils.json_to_sheet(items || []);
  workSheet['!cols'] = cols || [];

  if (type === 'csv') {
    const csvString = XLSX.utils.sheet_to_csv(workSheet);
    const blob = new Blob([csvString], {
      type: 'data:attachment/csv;charset=utf-8,'
    });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.target = '_blank';
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else if (type === 'excel') {
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, filename);
    XLSX.writeFile(workBook, `${filename}.xlsx`);
  }
};

export const fixTimezone = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  const tzOffset = date.getTimezoneOffset();
  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return null;
  }
  // return momentDate.add('minutes', tzOffset);
  return momentDate.add(tzOffset, 'minutes');
};

// export const fixTimezone = date => {
//   const ISO_FORMAT_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/;
//   if (ISO_FORMAT_REGEX.test(date)) {
//     return String(date).split('T')[0];
//   }
//   return date;
// };

export const getImg = (url = '', title = '') => {
  if (!url?.length) {
    return;
  }

  const filename = `${title}.jpg`;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const formatPhone = (value) => {
  return (
    value
      ?.replace(/\D+/g, '')
      ?.replace(/(\d{3})(\d{3})(\d{1,4})/, '($1) $2-$3') || ''
  );
};

/**
 * Downloads a template file from a blob response.
 *
 * @param {Object} response - Axios response with blob data
 * @param {string} recordType - 'tag' or 'release'
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const downloadTemplateBlob = async (response, recordType) => {
  const blob = response?.data;
  if (!blob) {
    return { success: false, error: 'No response data' };
  }

  const filename =
    recordType === 'release'
      ? 'release-batch-template.xlsx'
      : 'tag-batch-template.xlsx';

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true };
};

/**
 * Select a range of cells
 * @param {ExcelJS.Worksheet} sheet - Worksheet
 * @param  {string}   columnRange - Column Range ("A:B")
 * @return {Object[]}           - Selected cells
 */
export const selectSheetRange = (sheet, columnRange) => {
  const [start, end] = columnRange.split(':', 2);

  let endColumn = sheet.getColumn(end);
  let startColumn = sheet.getColumn(start);

  if (!endColumn) throw new Error('End column not found');
  if (!startColumn) throw new Error('Start column not found');

  endColumn = endColumn._number;
  startColumn = startColumn._number;
  const totalRows = sheet.actualRowCount;

  const data = [];
  for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
    const row = sheet.getRow(rowIndex);
    const rowData = [];

    for (let colIndex = startColumn; colIndex <= endColumn; colIndex++) {
      const cell = row.getCell(colIndex);
      rowData.push(cell.value);
    }

    data.push(rowData);
  }

  return data;
};
